import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './styles/tokens.css';
import './styles/skins.css';
import './styles/base.css';
import './styles/components.css';
import './styles/modes.css';
import './styles/screens.css';
import './styles/animations.css';

import './modes'; // side-effect: registers every mode with the engine
import { App } from './App';

const container = document.getElementById('root');
if (!container) throw new Error('ClueVine: #root element not found');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// Fade out the instant-paint splash (index.html) once the app has painted.
const splash = document.getElementById('cv-splash');
if (splash) {
  requestAnimationFrame(() => {
    splash.classList.add('cv-splash--hide');
    const remove = () => splash.remove();
    splash.addEventListener('transitionend', remove, { once: true });
    window.setTimeout(remove, 700);
  });
}

// When a freshly deployed service worker takes over, reload once so players get
// the new version automatically — but never under someone mid-puzzle or reading
// a result: wait until the page is hidden, or until they're back on Home with
// nothing on screen. (Guarded so the very first SW registration on a clean
// visit doesn't trigger a reload.)
if ('serviceWorker' in navigator) {
  const hadController = Boolean(navigator.serviceWorker.controller);
  let reloading = false;
  const reload = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };
  const idle = () => !document.querySelector('.cv-play, .cv-modal');

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return;
    if (document.visibilityState === 'hidden' || idle()) {
      reload();
      return;
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') reload();
    });
    const poll = window.setInterval(() => {
      if (idle()) {
        window.clearInterval(poll);
        reload();
      }
    }, 2000);
  });

  // An installed app can sit in the background for days without navigating, so
  // check for a new build whenever it comes back to the foreground.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    navigator.serviceWorker
      .getRegistration()
      .then((reg) => reg?.update())
      .catch(() => {});
  });
}
